import React, { useState, useEffect } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, IconButton, Collapse, Box, Typography, TextField, TablePagination
} from '@mui/material';
import {
    KeyboardArrowDown as KeyboardArrowDownIcon,
    KeyboardArrowUp as KeyboardArrowUpIcon,
    Collections as CollectionsIcon,
} from '@mui/icons-material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ChecklistRtlOutlinedIcon from '@mui/icons-material/ChecklistRtlOutlined';
import CommentIcon from '@mui/icons-material/Comment';
import Swal from 'sweetalert2';
import { myApi } from '../../../../config/axiosConfig';
import moment from 'moment-timezone';
const Cookies = require('js-cookie');

export default function ExpandableDataTable({
    rows: initialRows,
    onPhotoClick,
    onClickComment,
    onUpdateClick,
    refresh,
}) {
    const [rows, setRows] = useState([]);
    const [openRowIds, setOpenRowIds] = useState({});
    const [filterVehicle, setFilterVehicle] = useState('');
    const [filterDate, setFilterDate] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    // ─── 1️⃣ Normalizar y agrupar ──────────────────────────────────────
    useEffect(() => {
        // a) Convertir todo a { vehicleNumber, dia, registros_del_dia: [...] }
        const flat = initialRows.flatMap(r => {
            if (Array.isArray(r.registros_del_dia)) {
                return [{
                    vehicleNumber: r.vehicleNumber ?? r.unidad,
                    dia: typeof r.dia === 'string' ? r.dia : moment(r.dia).tz('America/Chicago').format('DD/MM/YYYY HH:mm'),
                    registros_del_dia: r.registros_del_dia
                }];
            }
            // caso “aplanado” (transformados previamente)
            return [{
                vehicleNumber: r.unidad,
                dia: typeof r.fechaHora === 'string'
                    ? r.fechaHora
                    :  moment(r.fechaHora).tz('America/Chicago').format('DD/MM/YYYY'),
                registros_del_dia: [r]
            }];
        });
        // b) Agrupar por vehicleNumber + YYYY-MM-DD
        const map = {};
        flat.forEach(item => {
            const day = item.dia.slice(0, 10);
            const key = `${item.vehicleNumber}_${day}`;
            if (!map[key]) {
                map[key] = {
                    vehicleNumber: item.vehicleNumber,
                    dia: item.dia,
                    registros_del_dia: [...item.registros_del_dia]
                };
            } else {
                map[key].registros_del_dia.push(...item.registros_del_dia);
            }
        });

        setRows(Object.values(map));
    }, [initialRows]);

    // ─── 2️⃣ Paginado, toggle y filtro ─────────────────────────────────
    const toggleRow = id => setOpenRowIds(prev => ({ ...prev, [id]: !prev[id] }));
    const handleChangePage = (_, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = e => {
        setRowsPerPage(+e.target.value);
        setPage(0);
    };

    const filteredRows = rows.filter(row => {
        const byVehicle = row.vehicleNumber.toString().includes(filterVehicle);
        const byDate = filterDate ? row.dia.slice(0, 10) === filterDate : true;
        return byVehicle && byDate;
    });
    const displayRows = filteredRows.slice(
        page * rowsPerPage, page * rowsPerPage + rowsPerPage
    );

    // ─── 3️⃣ Color de fila ───────────────────────────────────────────────
    const getRowBg = row => {
        const first = row.registros_del_dia[0] || {};
        if (first.validoDiesel === 1 && first.validoContabilidad === 1) return '#d4edda';
        if (first.validoContabilidad === 0) return '#f8d7da';
        if (first.validoDiesel === 1 && first.validoContabilidad == null) return '#fff3cd';
        return 'inherit';
    };

    // ─── 4️⃣ Verificación ────────────────────────────────────────────────
    const handleShowModalVerificar = async (id, perfil, usuario) => {
        const isCont = perfil == '7' || perfil == '1';
        const res1 = await Swal.fire({
            title: "¿La información es válida?",
            icon: "question",
            showDenyButton: isCont,
            showCancelButton: true,
            confirmButtonText: 'Si',
            denyButtonText: 'No',
            cancelButtonText: 'Cancelar',
            reverseButtons: true,
            allowOutsideClick: false,
        });
        if (res1.isConfirmed) {
            await verificarDatos(id, perfil, 1, usuario);
            refresh();
        } else if (res1.isDenied && isCont) {
            const { value: reason, isConfirmed } = await Swal.fire({
                title: "¿Por qué rechazaste?",
                input: "textarea",
                inputPlaceholder: "Escribe la razón...",
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Enviar",
                cancelButtonText: "Cancelar",
                allowOutsideClick: false,
                inputValidator: v => !v.trim() && "Debes ingresar una razón."
            });
            if (isConfirmed && reason) {
                await verificarDatos(id, perfil, 0, usuario, reason);
                refresh();
            }
        }
    };
    const verificarDatos = async (id, perfil, estado, usuario, razon) => {
        const cat = { 4: 'Diesel', 7: 'Contabilidad', 1: 'Contabilidad' };
        const obj = { id, estado, perfil: cat[perfil], usuario, ...(razon && { razonRechazo: razon }) };
        try {
            const resp = await myApi.put('/fuel_up/verificarDatos', obj);
            if (resp.data.success) {
                await Swal.fire({
                    icon: estado === 1 ? 'success' : 'info',
                    title: estado === 1 ? 'Aceptado' : 'Rechazado',
                    confirmButtonText: 'Ok'
                });
            }
        } catch {
            await Swal.fire({ icon: 'error', title: 'Error al verificar' });
        }
    };

    return (
        <Box>
            {/* filtros */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                    label="Vehicle" size="small"
                    value={filterVehicle}
                    onChange={e => setFilterVehicle(e.target.value)}
                />
                <TextField
                    label="Date" type="date" size="small"
                    InputLabelProps={{ shrink: true }}
                    value={filterDate}
                    onChange={e => setFilterDate(e.target.value)}
                />
            </Box>

            <TableContainer component={Paper}>
                <Table sx={{
                    borderCollapse: 'collapse',
                    '& td, & th': { border: '1px solid rgba(224,224,224,1)' }
                }}>
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            <TableCell>Vehicle Number</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell align="right">Records Count</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {displayRows.map(row => {
                            const id = `${row.vehicleNumber}_${row.dia.slice(0, 10)}`;
                            return (
                                <React.Fragment key={id}>
                                    <TableRow style={{ backgroundColor: getRowBg(row) }}>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => toggleRow(id)}>
                                                {openRowIds[id]
                                                    ? <KeyboardArrowUpIcon />
                                                    : <KeyboardArrowDownIcon />}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>{row.vehicleNumber[0]}</TableCell>
                                        <TableCell>
                                            {row.dia}
                                        </TableCell>
                                        <TableCell align="right">
                                            {row.registros_del_dia.length}
                                        </TableCell>
                                    </TableRow>

                                    <TableRow>
                                        <TableCell colSpan={4} sx={{ p: 0 }}>
                                            <Collapse in={openRowIds[id]} timeout="auto" unmountOnExit>
                                                <Box sx={{ m: 2 }}>
                                                    <Typography variant="subtitle1">
                                                        Detailed Records
                                                    </Typography>

                                                    {row.registros_del_dia.map((rec, idx) => {
                                                        // ➡️ Si trae rec.datos, lo usamos; si no, construimos un array filtrado:
                                                        const data = Array.isArray(rec.datos)
                                                            ? rec.datos
                                                            : Object.entries(rec)
                                                                .filter(([key]) =>
                                                                    ![
                                                                        'id',
                                                                        'validoDiesel',
                                                                        'validoContabilidad',
                                                                        'verificadoDiesel',
                                                                        'verificadoContabilidad',
                                                                        'unidad',
                                                                        "tipoUnidad",
                                                                        "caja",
                                                                        "placas",
                                                                        "placasEstado",
                                                                        "fleet",
                                                                        "flota",
                                                                        "numOrden",
                                                                    ].includes(key)
                                                                )
                                                                .map(([label, value]) => ({
                                                                    label,
                                                                    value:
                                                                        value instanceof Date
                                                                            ? value.toLocaleString()
                                                                            : value,
                                                                }));

                                                        const visibleData = data.filter(d => {
                                                            // si es null o undefined, lo quitamos
                                                            if (d.value == null) return false;
                                                            // si es un array [value, id_rel_flujo], y el value es null, también lo quitamos
                                                            if (Array.isArray(d.value) && d.value[0] == null) return false;
                                                            return true;
                                                        });


                                                        return (
                                                            <Box key={idx} sx={{ mb: 2, p: 2, border: 1, borderColor: 'grey.300', borderRadius: 1 }}>
                                                                <Table size="small" sx={{
                                                                    borderCollapse: 'collapse',
                                                                    '& td, & th': { border: '1px solid rgba(224,224,224,1)' }
                                                                }}>
                                                                    <TableHead>
                                                                        <TableRow style={{ backgroundColor: getRowBg(row) }}>
                                                                            <TableCell>Acciones</TableCell>
                                                                            {visibleData.map((d, i) => (
                                                                                <TableCell key={i}>{d.label}</TableCell>
                                                                            ))}
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        <TableRow>
                                                                            <TableCell align="center">
                                                                                <IconButton size="small" onClick={() => onUpdateClick(rec)}>
                                                                                    <EditNoteIcon fontSize="small" color="warning" />
                                                                                </IconButton>
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={() =>
                                                                                        handleShowModalVerificar(
                                                                                            rec.id,
                                                                                            Cookies.get('ebt_perfil'),
                                                                                            Cookies.get('ebt_id_user')
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    <ChecklistRtlOutlinedIcon fontSize="small" color="primary" />
                                                                                </IconButton>
                                                                                <IconButton size="small" onClick={() => onClickComment(rec.id)}>
                                                                                    <CommentIcon fontSize="small" color="primary" />
                                                                                </IconButton>
                                                                                <IconButton size="small" onClick={() => onPhotoClick(rec.id, null)}>
                                                                                    <CollectionsIcon fontSize="small" color="primary" />
                                                                                </IconButton>
                                                                            </TableCell>
                                                                            {visibleData.map((d, i) => {
                                                                                // Sólo mostramos el icono en estas etiquetas
                                                                                const allowedPhotoLabels = ['operador', 'odometroInicialBomba', 'odometroFinalBomba', 'galonesCargados', 'horasMotor', 'nivelTanque', 'temperatura', 'infoViaje', 'odometroCamion', 'rendimiento', 'transportista'];
                                                                                // Desempaquetamos valor y flujo
                                                                                const [val, flujo] = Array.isArray(d.value) ? d.value : [d.value, null];

                                                                                return (
                                                                                    <TableCell align="center" key={i}>
                                                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                                            <Typography sx={{ mr: 1 }}>{val}</Typography>
                                                                                            {allowedPhotoLabels.includes(d.label) && flujo != null && (
                                                                                                <IconButton
                                                                                                    size="small"
                                                                                                    onClick={() => onPhotoClick(rec.id, flujo)}
                                                                                                >
                                                                                                    <CollectionsIcon fontSize="small" color="primary" />
                                                                                                </IconButton>
                                                                                            )}
                                                                                        </Box>
                                                                                    </TableCell>
                                                                                );
                                                                            })}
                                                                        </TableRow>
                                                                    </TableBody>
                                                                </Table>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>

                <TablePagination
                    component="div"
                    count={filteredRows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25]}
                />
            </TableContainer>
        </Box>
    );
}